import { useMemo } from 'react';
import { MultiWebClientMerchantModel } from '@/User/domain';
import { normalize } from '../utils/normalize';

/**
 * Hook personalizado para filtrar comercios basándose en el término de búsqueda.
 * Implementa búsqueda insensible a acentos con un mínimo de 3 caracteres.
 * 
 * @param merchants - Lista completa de comercios
 * @param searchTerm - Término de búsqueda ingresado por el usuario
 * @returns Lista filtrada de comercios con sus sub-items coincidentes
 */
export const useMerchantFilter = (
  merchants: MultiWebClientMerchantModel[],
  searchTerm: string
): MultiWebClientMerchantModel[] => {
  return useMemo(() => {
    // Si la búsqueda tiene menos de 3 caracteres, devolver todos los comercios
    if (searchTerm.length < 3) {
      return merchants;
    }

    const normalizedSearch = normalize(searchTerm);

    // Función para verificar si la cadena de búsqueda está presente en algún campo
    const matchesSearch = (name: string, cif: string, code: string) => {
      return normalize(name).includes(normalizedSearch) ||
             normalize(cif).includes(normalizedSearch) ||
             normalize(code.toString()).includes(normalizedSearch);
    };

    // Filtrar comercios y sus sub-items
    return merchants
      .map(merchant => {
        // Verificar si el comercio principal coincide
        const matchMain = matchesSearch(
          merchant.name,
          merchant.cif,
          merchant.code.toString()
        );

        // Filtrar sub-items que coinciden con la búsqueda
        const matchingSubs = Array.isArray(merchant.subItems)
          ? merchant.subItems.filter(sub =>
              matchesSearch(sub.name, sub.cif, sub.code.toString())
            )
          : [];

        // Si coincide el principal o hay sub-items que coinciden, incluir el comercio
        if (matchMain || matchingSubs.length > 0) {
          return {
            ...merchant,
            subItems: matchingSubs.length > 0 ? matchingSubs : merchant.subItems
          };
        }

        return undefined;
      })
      .filter((m): m is MultiWebClientMerchantModel => m !== undefined);
  }, [merchants, searchTerm]);
};
